import { enableProdMode } from '@angular/core';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';
import {
  EnvironmentDirectory,
  applyEnvironmentEntry,
  getPersistedEnvKey,
  normalizeEnvironmentDirectory,
  persistEnvKey,
  showEnvPickerOverlay
} from './app/shared/env-switcher';
import { FALLBACK_ENVIRONMENT_DIRECTORY } from './environments/environment-directory-fallback';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/service-worker.js')
    .then(registration => {
      console.log('Service Worker registered:', registration);
    })
    .catch(error => {
      console.error('Service Worker registration failed:', error);
    });
}

if (environment.isInternalBuild) {
  // Internal (QA) build only (SHFT-5757): resolve which backend to point at
  // BEFORE Angular bootstraps, so the picker appears ahead of the login page
  // ever rendering. Uses the modular Firebase SDK directly, since Angular's
  // DI/module system doesn't exist yet at this point.
  runInternalBootstrapGate();
} else if (environment.environmentKey) {
  // dev / dev-alt-N builds (revised SHFT-5757 design): silently consult
  // Remote Config for THIS build's own fixed environment key and apply an
  // override if one is published - no picker, no user interaction. Lets the
  // team update a dev-alt's backend URL/tenant via Remote Config without a
  // redeploy, same benefit as the internal build gets, without the UX of
  // choosing an environment (there's nothing to choose - it's fixed).
  runSilentEnvironmentOverrideGate();
} else {
  // staging/production: unchanged - never consults Remote Config for these
  // values at all.
  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch(err => console.error(err));
}

async function fetchEnvironmentDirectory(): Promise<EnvironmentDirectory> {
  try {
    const app = getApps().length ? getApp() : initializeApp(environment.firebaseConfig);
    const remoteConfig = getRemoteConfig(app);
    remoteConfig.defaultConfig = {
      environment_directory: JSON.stringify(FALLBACK_ENVIRONMENT_DIRECTORY)
    };
    // TODO(SHFT-5757): tune minimumFetchIntervalMillis for the internal build
    // if the default (12h) turns out to be too sticky for QA switching envs.
    await fetchAndActivate(remoteConfig);
    const raw = getValue(remoteConfig, 'environment_directory').asString();
    const normalized = normalizeEnvironmentDirectory(JSON.parse(raw));
    if (normalized) {
      return normalized;
    }
  } catch (err) {
    console.error('Failed to fetch environment_directory from Remote Config, falling back to bundled defaults', err);
  }
  return FALLBACK_ENVIRONMENT_DIRECTORY;
}

async function runInternalBootstrapGate(): Promise<void> {
  const directory = await fetchEnvironmentDirectory();

  const persistedKey = getPersistedEnvKey(directory);
  const chosenKey = await showEnvPickerOverlay(directory, persistedKey);
  persistEnvKey(chosenKey);

  const entry = directory[chosenKey] || directory[persistedKey];
  if (entry) {
    applyEnvironmentEntry(environment, entry);
  }

  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch(err => console.error(err));
}

async function runSilentEnvironmentOverrideGate(): Promise<void> {
  const directory = await fetchEnvironmentDirectory();

  const entry = directory[environment.environmentKey];
  if (entry) {
    applyEnvironmentEntry(environment, entry);
  }

  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch(err => console.error(err));
}
